/**
 * Login session of an SSO proxy in front of the app (e.g. oauth2-proxy):
 * who is logged in and where to log out. The proxy's /oauth2/userinfo tells
 * the user, the reverse proxy adds the logout address as an X-Logout-Url
 * header. Without such a proxy (or without a session, e.g. from a network
 * that skips the login) there is nothing to log out of and the "Log out"
 * items stay hidden.
 */

import { useEffect, useState } from "react";

import { SyncEngine } from "./sync";

export type ProxySession = {
  user: string;
  logoutUrl: string;
};

let session: Promise<ProxySession | null> | null = null;

export const getProxySession = () => {
  session ??= (async () => {
    try {
      const response = await fetch("/oauth2/userinfo", {
        cache: "no-store",
        credentials: "same-origin",
      });
      const logoutUrl = response.headers.get("X-Logout-Url");
      // without a proxy the SPA fallback answers with HTML
      if (
        !response.ok ||
        !logoutUrl ||
        !response.headers.get("Content-Type")?.includes("application/json")
      ) {
        return null;
      }
      const info = await response.json();
      return {
        user: info.preferredUsername || info.user || info.email || "",
        logoutUrl,
      };
    } catch {
      return null;
    }
  })();
  return session;
};

export const useProxySession = () => {
  const [value, setValue] = useState<ProxySession | null>(null);
  useEffect(() => {
    let active = true;
    getProxySession().then((result) => active && setValue(result));
    return () => {
      active = false;
    };
  }, []);
  return value;
};

/** pushes pending changes first (they would stay on this device anyway and
 * sync after the next login) */
export const logOut = async (proxySession: ProxySession) => {
  try {
    await SyncEngine.syncNow();
  } finally {
    window.location.assign(proxySession.logoutUrl);
  }
};
